import { Component, EventEmitter, inject, OnInit, Output } from '@angular/core';
import { BaryModalEvent, IBaryModalEvent } from '../../../../../../models/bary-modal-event';
import { BaryComponentHelper } from '../../../../util/bary-component-helper';
import { MDBModalRef, MDBModalService } from 'ng-uikit-pro-standard';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { ReasonRefund } from '../../../../../../models/enums/reason-refund.enum';
import { TransportOrderModificationService } from '../../../../../../services/transport-order-modification.service';
import { TransportOrderModificationCauseService } from '../../../../../../services/transport-order-modification-cause.service';
import { CartGroupValueItem } from '../../../cart/cart-group-value/cart-group-value-item.model';
import { CalculationMethodEnum, ModificationTypeEnum, TransportOrderModification } from '../../../../../../models/transport-order-modification.model';
import { TransportOrderModificationCause } from '../../../../../../models/transport-order-modification-cause.model';
import { TransportOrderView } from '../../../../../../models/transport-order-view.model';
import { NgxSpinnerService } from 'ngx-spinner';
import { BaryModalErrorRefundPayzenComponent } from '../bary-modal-error-refund-payzen/bary-modal-error-refund-payzen.component';

@Component({
  selector: 'jhi-bary-modal-transport-refund',
  templateUrl: './bary-modal-transport-refund.component.html',
  styleUrls: ['./bary-modal-transport-refund.component.scss'],
})
export class BaryModalTransportRefundComponent implements OnInit {
  readonly #modalRef = inject(MDBModalRef);
  readonly #transportOrderModificationService = inject(TransportOrderModificationService);
  readonly #transportOrderModificationCauseService = inject(TransportOrderModificationCauseService);
  readonly #translateService = inject(TranslateService);
  readonly #spinnerService = inject(NgxSpinnerService);
  readonly #mdbModalService = inject(MDBModalService);

  public action = BaryComponentHelper.generateAction<IBaryModalEvent>();
  public transport!: TransportOrderView;
  public form!: FormGroup;
  public emptyReason = ReasonRefund.EMPTY_REASON;
  public option: { label: string; value: any }[] = [];
  public acceptAllRefunds: FormControl = new FormControl(false);

  public emptyMotif: string = this.#translateService.instant('MODAL_REFUND_BILL.EMPTY_REASON');
  @Output() closeEvent = new EventEmitter();

  public ngOnInit(): void {
    this.#transportOrderModificationCauseService.getByModificationType(ModificationTypeEnum.REFUND).subscribe({
      next: causes => {
        causes.forEach((val: any) => this.option.push({ label: val.value, value: val }));
        this.initForm();
      },
    });
  }

  private initForm(): void {
    this.form = new FormGroup({
      refundAmount: new FormControl(0, [this.checkRefundAmount(), Validators.max(this.getMaximumRefundable()), Validators.required]),
      reason: new FormControl(ReasonRefund.EMPTY_REASON, [Validators.required, this.checkReason()]),
      contractor: new FormControl('', [Validators.required]),
    });
  }

  public checkReason(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.value === this.emptyReason) {
        return { required: true };
      }
      return null;
    };
  }

  public checkRefundAmount(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.value === 0) {
        return { required: true };
      }
      return null;
    };
  }

  public getBillHTItems(): CartGroupValueItem[] {
    const items: CartGroupValueItem[] = [];
    items.push(
      new CartGroupValueItem({
        value: this.transport.gazoleTax!,
        label: this.#translateService.instant('MODAL_REFUND_BILL.SUB_BLOCK.WITH_GAZOLE_TAX', { gazoleTaxRate: this.transport.gazoleTaxRate }),
      })
    );
    if (this.transport.insurance) {
      items.push(
        new CartGroupValueItem({
          value: this.transport.insurance,
          label: this.#translateService.instant('MODAL_REFUND_BILL.SUB_BLOCK.WITH_INSURANCE'),
        })
      );
    }
    return items;
  }

  public getRefundVat(): number {
    return Math.min(this.calculateTaxes(), this.getTransportVAT());
  }

  public getRefundTTC(): number {
    return Math.min(this.getRefundHT() + this.calculateTaxes(), this.getTransportTTC());
  }

  public calculateTaxes(): number {
    return this.form.value.refundAmount * 0.2;
  }

  public handleClick(): void {
    this.#modalRef.hide();
  }

  public handleSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.#spinnerService.show();
    const result = {
      modificationType: ModificationTypeEnum.REFUND,
      contractor: this.form.value.contractor,
      createdDate: new Date(),
      date: new Date(),
      calculationMethod: CalculationMethodEnum.ADD_FIXED,
      value: this.form.value.refundAmount,
      amount: this.form.value.refundAmount,
      transportOrderId: this.transport.id,
      cause: this.form.value.reason as TransportOrderModificationCause,
    } as TransportOrderModification;
    this.#transportOrderModificationService.saveAll(this.transport.basketOrder?.id!, [result]).subscribe({
      next: (): void => {
        this.action.next(new BaryModalEvent({ first: true }));
        this.#modalRef.hide();
        this.#spinnerService.hide();
      },
      error: (e: any): void => {
        this.#spinnerService.hide();
        this.#modalRef.hide();
        this.#mdbModalService.show(BaryModalErrorRefundPayzenComponent, {
          data: {
            errorMessage: e?.baryError?.errorParams['errorMessage'],
            errorCode: e?.baryError?.errorParams['errorCode'],
          },
          animated: true,
          ignoreBackdropClick: true,
          class: 'modal-lg',
        });
      },
    });
  }

  public getRefundHT(): number {
    return Math.min(this.form.value.refundAmount, this.getMaximumRefundable());
  }

  getTransportPriceHTWithGazoleTax(): number {
    return this.transport?.priceHT! + this.transport.gazoleTax!;
  }

  getTransportVAT(): number {
    return this.getTransportPriceHTWithGazoleTax() * 0.2;
  }

  getTransportTTC(): number {
    return this.getTransportPriceHTWithGazoleTax() + this.getTransportVAT();
  }

  getAlreadyRefundTTC(): number {
    if (this.transport.refundAmount && this.transport.vatRate) {
      return this.transport.refundAmount + this.transport.refundAmount * (this.transport.vatRate / 100);
    }
    return 0;
  }

  getMaximumRefundable(): number {
    return this.getTransportPriceHTWithGazoleTax() - this.transport.refundAmount!;
  }
}
